import type { CSSProperties } from 'react'
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui'
import GradientSphereCard from './components/GradientSphereCard'
import './healthAgingPaceCard.css'

const PACE_VALUE = 1.4
const SCALE_MIN = -1
const SCALE_MAX = 3
const BAR_COUNT = 56
const BAR_HEIGHT_PX = 78
const MARKER_INDEX = Math.round(((PACE_VALUE - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * (BAR_COUNT - 1))

interface PaceBar {
  isEmphasis: boolean
  isMarker: boolean
  opacity: number
  height: number
}

interface PaceBarStyle extends CSSProperties {
  '--pace-bar-height'?: string
  '--pace-bar-opacity'?: number
}

function createBars(): PaceBar[] {
  return Array.from({ length: BAR_COUNT }, (_, index) => {
    const distanceFromMarker = Math.abs(index - MARKER_INDEX)
    const baseOpacity = 0.2 + (1 - Math.min(distanceFromMarker, 18) / 18) * 0.18

    return {
      isMarker: index === MARKER_INDEX,
      isEmphasis: distanceFromMarker > 0 && distanceFromMarker <= 4,
      opacity: Number(baseOpacity.toFixed(3)),
      height: BAR_HEIGHT_PX,
    }
  })
}

const BARS = createBars()

export default function HealthAgingPaceCard() {
  return (
    <GradientSphereCard
      as="article"
      className="health-aging-pace-card"
      sphereColor="rgba(157, 163, 175, 0.34)"
      ariaLabel="Pace of aging analysis"
    >
      <CardHeader className="health-aging-pace-card__header flex-row items-center justify-between gap-3 space-y-0 p-0">
        <CardTitle className="health-aging-pace-card__title">Pace of Aging</CardTitle>
        <CardDescription className="health-aging-pace-card__trend-pill">
          <span aria-hidden="true" className="health-aging-pace-card__trend-icon" />
          slower vs. last week
        </CardDescription>
      </CardHeader>

      <CardContent className="p-0">
        <div className="health-aging-pace-card__value-row">
          <p className="health-aging-pace-card__side-label">
            <span aria-hidden="true" className="health-aging-pace-card__slow-icon" />
            Slow
          </p>
          <p className="health-aging-pace-card__value">{PACE_VALUE.toFixed(1)}x</p>
          <p className="health-aging-pace-card__side-label health-aging-pace-card__side-label--end">
            Fast
            <span aria-hidden="true" className="health-aging-pace-card__fast-icon" />
          </p>
        </div>

        <div className="health-aging-pace-card__bars" role="img" aria-label={`Pace marker at ${PACE_VALUE.toFixed(1)}x`}>
          {BARS.map((bar, index) => {
            const style: PaceBarStyle = {
              '--pace-bar-height': `${bar.height}px`,
              '--pace-bar-opacity': bar.opacity,
            }

            return (
              <span
                key={`pace-bar-${index}`}
                className={`health-aging-pace-card__bar${bar.isMarker ? ' health-aging-pace-card__bar--marker' : ''}${
                  bar.isEmphasis ? ' health-aging-pace-card__bar--emphasis' : ''
                }`}
                style={style}
                aria-hidden="true"
              />
            )
          })}
        </div>
      </CardContent>

      <CardFooter className="health-aging-pace-card__scale justify-between p-0">
        <span>-1.0x</span>
        <span>1.0x</span>
        <span>3.0x</span>
      </CardFooter>
    </GradientSphereCard>
  )
}
